import { useEffect, useId, useState } from "react"
import type { PortalForwarding } from "@ledgerbox/domain"

import { usePortalApi } from "./api-context"

/**
 * The client's forward-to address, in the help sheet. Read each time the
 * sheet opens, so a Gmail code that arrived while the client was setting up
 * the rule shows up without a reload. Shows nothing while it loads, when it
 * fails, or when the firm has no forwarding: it is an extra way to send, and
 * the sheet reads fine without it.
 */
export function PortalForwardingSection({
  open,
  firmName,
}: {
  open: boolean
  firmName: string
}) {
  const api = usePortalApi()
  const [forwarding, setForwarding] = useState<PortalForwarding | null>(null)
  const [copyNote, setCopyNote] = useState<string | null>(null)
  const titleId = useId()

  useEffect(() => {
    if (!open) return
    let active = true
    setCopyNote(null)
    api
      .getForwarding()
      .then((response) => {
        if (active) setForwarding(response.forwarding)
      })
      .catch(() => {
        if (active) setForwarding(null)
      })
    return () => {
      active = false
    }
  }, [api, open])

  if (!forwarding) return null

  const copy = async (value: string, label: string) => {
    setCopyNote(null)
    try {
      await navigator.clipboard.writeText(value)
      setCopyNote(`${label} copied`)
    } catch {
      setCopyNote(
        `Could not copy the ${label.toLowerCase()}. Select and copy it instead.`
      )
    }
  }

  const gmail = forwarding.gmailConfirmation

  return (
    <section className="help-forwarding" aria-labelledby={titleId}>
      <h3 id={titleId}>Email documents</h3>
      <p>
        Forward bills, receipts and statements to this address. {firmName}{" "}
        gets the attachments.
      </p>
      <div className="engagement-copy-row">
        <strong className="mono">{forwarding.address}</strong>
        <button
          type="button"
          className="text-button"
          onClick={() => void copy(forwarding.address, "Address")}
        >
          Copy address
        </button>
      </div>
      {gmail ? (
        <div className="help-gmail">
          <h3>Gmail forwarding code</h3>
          <p>
            Gmail sent this code to confirm forwarding from{" "}
            <span className="mono">{gmail.gmailAddress}</span>. Enter it in
            Gmail, or confirm with the link.
          </p>
          <div className="engagement-copy-row">
            <strong className="mono help-gmail-code">{gmail.code}</strong>
            <button
              type="button"
              className="text-button"
              onClick={() => void copy(gmail.code, "Code")}
            >
              Copy code
            </button>
          </div>
          {gmail.confirmUrl ? (
            <a
              className="ghost-pill small"
              href={gmail.confirmUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Confirm in Gmail
            </a>
          ) : null}
        </div>
      ) : null}
      {copyNote ? (
        <p role="status" className="help-forwarding-note">
          {copyNote}
        </p>
      ) : null}
    </section>
  )
}
